import { EventMapPanel } from '@/components/event-maps/EventMapPanel';
import { useAuth } from '@/lib/use-auth';
import { EmptyState, PageHeader, Panel, PanelHeader } from '@/components/ui';

/*
 * Event maps — the two look-up tables the public 2027 interest form resolves
 * against.
 *
 * ── 🔴 WHY THIS SCREEN EXISTS ─────────────────────────────────────────────
 *
 * `admin-app` posts a submission naming festivals by `event_key` and cities by
 * `city_key`. A key that resolves to nothing is a **422 at submission time**, so
 * **until a mapping row exists the public form cannot be submitted at all** —
 * an unmapped key is not a blank cell somewhere in EMERGE, it is a customer
 * pressing Send and being refused. Until this page shipped there was no way for
 * a person to enter one: no screen, no endpoint, only SQL against production.
 *
 * ── ⚠️ IT IS A CONFIGURATION SCREEN, NOT A DAILY ONE ──────────────────────
 *
 * Somebody sits down ONCE with a list of keys from `admin-app` and matches seven
 * festivals and nineteen cities. Everything here is shaped by that and not by
 * the habits of a list page:
 *
 *  - **No pager, no search, no status filter.** The job is *"is every key
 *    mapped, and mapped to the right thing"* — a question about COMPLETENESS
 *    that page 1 of N cannot answer, because a missing mapping and a mapping on
 *    the next page look identical. The lists are read whole or they refuse.
 *  - **Both tables are on screen at once**, because the job is both of them. A
 *    tab would hide half of it. ⚠️ This is NOT the `/operations` case, where one
 *    pill mounts at a time: that costs ~21 requests, puts three `<h1>`s in one
 *    `<main>` and gives three saved-filter writers one query string. Here it is
 *    four reads, one `<h1>`, and no saved filters at all.
 *  - **Retired mappings are never hidden by default.** A key the form has
 *    stopped accepting is exactly what somebody is hunting.
 *
 * ── 🔴 THE FAILURE EVERY DECISION HERE IS ABOUT ───────────────────────────
 *
 * **A mapping that points at the WRONG project is accepted cleanly and is
 * indistinguishable afterwards.** It files an institution's answers against the
 * wrong festival with a **201**; the key that produced it is audit only and
 * joined on by nothing, so no artefact anywhere disagrees with it. A 2026
 * festival and its 2027 successor have nearly the same name — `Project.name` is
 * explicitly **not unique** — so the name alone does not tell them apart either.
 *
 * So the project's SEASON (its cycle, its intake, when it runs) is rendered
 * everywhere a person could be about to be wrong: in the picker's searchable
 * hint, under the picker once something is chosen, and in the table cell. That
 * costs a second read of the projects master on a screen that already has the
 * mappings, and it is the whole point of the screen.
 *
 * ── Who can open it ───────────────────────────────────────────────────────
 *
 * ADMIN ONLY, and the API agrees: every route on `/event-maps` carries
 * `requireRole(...ADMIN_ROLES)` on the READ as well as the writes, so below that
 * tier the only possible answer is a refusal. That is the `/audit-log` and
 * `/settings/smtp` shape, not the `/integrations` one — which is why the nav row
 * is `adminOnly` rather than gated on a permission module.
 *
 * ⚠️ **THE ROUTE CARRIES NO GUARD**, on this app's standing rule: a hidden row
 * is an optimisation and the server's 403 is the guarantee, so somebody arriving
 * by URL must meet the page's own refusal rather than a redirect or a blank
 * screen. **No request is issued at all for a reader who cannot be served** —
 * two empty tables would read as "nothing is mapped", which on this screen is a
 * different and alarming false statement.
 *
 * A real 403 mid-session is still handled, inside each panel: a role can change
 * under a live access token and that token lasts days.
 */
export function EventMapsPage() {
  const { ability } = useAuth();

  /* ── Not an administrator: explain, and never fetch ──────────────────── */

  /*
   * 🔴 2026-10-01: the backend gates every route here on the `settings` module
   * (`requireGrant('settings', …)` in `event-maps.routes.ts`), and the set
   * decides for every role. *SUPERSEDED: `!ability.isAdmin`.*
   * 2026-10-02 (owner): the maps are their own module, `settings-maps`.
   */
  if (!ability.canRead('settings-maps')) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="2027 interest form" title="Event maps" />
        <Panel aria-labelledby="event-maps-denied-heading">
          <PanelHeader id="event-maps-denied-heading" title="Event maps" />
          <EmptyState
            icon={<MapIcon />}
            title="Not available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  /* ── The page ────────────────────────────────────────────────────────── */

  return (
    <div className="grid min-w-0 gap-4">
      {/*
        * ⚠️ **ONE `<h1>`, from `PageHeader`**, with each panel's heading below
        * it — `AppShell` renders the `<main>`, so this page adds none of its own
        * and the two panels are sections within one document outline.
        */}
      <PageHeader eyebrow="2027 interest form" title="Event maps" />

      {/*
        * ⚠️ **Festivals first, and the order is not alphabetical.** A city key is
        * only ever asked about a festival that tours, so the festival table is
        * the one somebody fills in first and the one an unmapped city is a
        * consequence of.
        */}
      <EventMapPanel kind="project" headingId="event-maps-projects-heading" />
      <EventMapPanel kind="city" headingId="event-maps-cities-heading" />
    </div>
  );
}

function MapIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M9 4.5 4 6.5v13l5-2 6 2 5-2v-13l-5 2Z" />
      <path d="M9 4.5v13" />
      <path d="M15 6.5v13" />
    </svg>
  );
}
